import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Image } from 'antd';
import { BinaryChoiceEnum } from 'constants/appointments';
import type { HealthDataFieldsType } from 'types/patients';

import { PatientHealthData } from './index';
import { ItemTitle, ItemValue, HealthItem, StyledIcon } from './styled';
import alcohol from '../../../assets/icons/health/alcohol.svg';
import allergies from '../../../assets/icons/health/allergies.svg';
import chronic_conditions from '../../../assets/icons/health/chronic_conditions.svg';
import disabilities from '../../../assets/icons/health/disabilities.svg';
import family_history from '../../../assets/icons/health/family_history.svg';
import heightIcon from '../../../assets/icons/health/height.svg';
import medication from '../../../assets/icons/health/medication.svg';
import medication_history from '../../../assets/icons/health/medication_history.svg';
import pregnant from '../../../assets/icons/health/pregnant.svg';
import regular_cycle from '../../../assets/icons/health/regular_cycle.svg';
import sleep from '../../../assets/icons/health/sleep.svg';
import smoke from '../../../assets/icons/health/smoke.svg';
import surgical_history from '../../../assets/icons/health/surgical_history.svg';
import vaccination from '../../../assets/icons/health/vaccination.svg';
import weightIcon from '../../../assets/icons/health/weight.svg';

const iconMapping = {
  height: heightIcon,
  weight: weightIcon,
  chronic_conditions: chronic_conditions,
  family_history: family_history,
  allergies: allergies,
  surgical_history: surgical_history,
  medication: medication,
  medication_history: medication_history,
  vaccination: vaccination,
  disabilities: disabilities,
  smoke: smoke,
  alcohol: alcohol,
  sleep: sleep,
  pregnant: pregnant,
  regular_cycle: regular_cycle
};

interface IProps extends HealthDataFieldsType {
  patientData: PatientHealthData;
}

const HealthDetailsItem: FC<IProps> = ({
  title,
  gender,
  stepKey,
  unitKey,
  otherKey,
  optionsKey,
  patientData
}) => {
  const { t } = useTranslation();
  const healthData = patientData.health_info || {};

  const renderUnit = () => {
    if (!unitKey) {
      return '';
    }

    const unit = healthData[unitKey] as string;

    return t(`health_details.${unit}`);
  };

  const renderOther = (hasOptions: boolean, closeScope?: boolean) => {
    if (!otherKey) {
      return closeScope ? ')' : '';
    }

    const other = healthData[otherKey];

    if (other) {
      return `${hasOptions ? ', ' : '('}${other})`;
    }

    return closeScope ? ')' : '';
  };

  const renderYesOptions = () => {
    if (!optionsKey) {
      return renderOther(false);
    }

    const options = healthData[optionsKey];

    if (Array.isArray(options) && options.length) {
      const value = options.map(item => t(`health_details.${item}`)).join(', ');

      return `(${value}${renderOther(!!value, true)}`;
    }

    if (typeof options === 'string' && options) {
      return `(${t(`health_details.${options}`)}${renderOther(true, true)}`;
    }

    return renderOther(false);
  };

  const renderHealthInfo = () => {
    const value = healthData[stepKey];

    switch (value) {
      case BinaryChoiceEnum.Yes:
        return `${t('health_details.yes')} ${renderYesOptions()}`;
      case BinaryChoiceEnum.No:
        return t('health_details.no');
      default:
        if (typeof value === 'string') {
          return `${
            unitKey ? value : t(`health_details.${value}`)
          }${renderUnit()}`;
        }

        return null;
    }
  };

  if (gender && gender !== patientData?.gender) {
    return null;
  }

  return (
    <HealthItem>
      <StyledIcon>
        <Image
          src={iconMapping[stepKey]}
          alt={title}
          width={28}
          height={28}
          preview={false}
        />
      </StyledIcon>
      <Flex vertical gap={4}>
        <ItemTitle>{t(`health_details.${title}`)}</ItemTitle>
        <ItemValue>{renderHealthInfo()}</ItemValue>
      </Flex>
    </HealthItem>
  );
};

export default memo(HealthDetailsItem);
